import React from 'react';
import { Alert } from 'react-bootstrap';
import { PairFrameLens } from '../../../models/PairFrameLens';
import { CustomerRecipe } from '../../../models/CustomerRecipe';
import { strings } from '../../../resources/strings';
import { FormatHelper } from '../../../helpers/FormatHelper';
import { ProductRecipeLinkingRequest } from '../../../models/ProductRecipeLinkingRequest';
import { Field, FieldType } from '../../../common-components/Field/Field';
import { ProductRecipeLinkingRequestItem } from '../../../models/ProductRecipeLinkingRequestItem';

interface ICustomerRecipeLinkingFormProps {
  pairsFrameLens: PairFrameLens[];
  customerRecipes: CustomerRecipe[];
  linkingRequests: ProductRecipeLinkingRequest[];
  setLinkingRequests: (value: ProductRecipeLinkingRequest[]) => void;
}

const CustomerRecipeLinkingForm: React.FC<ICustomerRecipeLinkingFormProps> = ({
  pairsFrameLens,
  customerRecipes,
  linkingRequests,
  setLinkingRequests
}) => {

  const render = () => {
    if (!pairsFrameLens.length) {
      return <Alert variant='warning' style={{ marginTop: '10px' }}>Pedido não possui pares/grupos de "armação-lente".</Alert>;
    }

    const options = [
      { id: 0, name: strings.None }
    ];
    customerRecipes.forEach(recipe => {
      options.push({
        id: recipe.id,
        name: recipe.name && recipe.description
          ? `${recipe.description} (${recipe.name}) - ${FormatHelper.formatDateTime(recipe.createdAt)}`
          : recipe.name || recipe.description
            ? `${recipe.description || ''} ${recipe.name || ''} - ${FormatHelper.formatDateTime(recipe.createdAt)}`
            : FormatHelper.formatDateTime(recipe.createdAt)
      });
    });

    return (
      <div>
        {pairsFrameLens.map((pairFrameLens, index) => {
          const links = linkingRequests?.filter(lr =>
            lr.pairNumber === pairFrameLens.pairNumber &&
            lr.items.every(lri => pairFrameLens.items.some(pfli => pfli.id === lri.orderItemId))) || [];
          const fieldValue = links.length === 1 ? links[0].customerRecipeId : 0;
          return (
            <Field
              key={`field-frame-lens-${index}`}
              type={FieldType.Select}
              label={pairFrameLens.name}
              selectProps={{
                options: [...options],
                displayField: 'name',
                keyField: 'id'
              }}
              value={fieldValue}
              textMuted={links.length > 1 ? 'Itens/produtos vinculados a mais de uma receita. Resolva a vinculação duplicada deste item/produto.' : ''}
              change={(value) => changeSelectedCustomerRecipe(value, pairFrameLens)}
            />
          );
        })}
      </div>
    )
  }

  return render();

  function changeSelectedCustomerRecipe(customerRecipeId: number, pairFrameLens: PairFrameLens) {
    customerRecipeId = +customerRecipeId || 0;
    const filteredLinkingRequests = getLinkingRequestsByPairFrameLens(pairFrameLens);
    if (filteredLinkingRequests.length === 0 && customerRecipeId > 0) {
      setLinkingRequests([
        ...linkingRequests,
        createLinkingRequest(customerRecipeId, pairFrameLens)
      ]);
    } else if (filteredLinkingRequests.length > 0 && customerRecipeId === 0) {
      setLinkingRequests(removeLinkingRequestsByPairFrameLens(pairFrameLens));
    } else if (filteredLinkingRequests.length > 0 && customerRecipeId > 0) {
      setLinkingRequests([
        ...removeLinkingRequestsByPairFrameLens(pairFrameLens),
        createLinkingRequest(customerRecipeId, pairFrameLens)
      ]);
    }
  }

  function getLinkingRequestsByPairFrameLens(pairFrameLens: PairFrameLens) {
    return linkingRequests.filter(lr =>
      lr.pairNumber === pairFrameLens.pairNumber &&
      lr.items.every(lri => pairFrameLens.items.some(pfli => pfli.id === lri.orderItemId)));
  }

  function removeLinkingRequestsByPairFrameLens(pairFrameLens: PairFrameLens) {
    return linkingRequests.filter(lr =>
      lr.pairNumber !== pairFrameLens.pairNumber ||
      !lr.items.every(lri => pairFrameLens.items.some(pfli => pfli.id === lri.orderItemId)));
  }

  function createLinkingRequest(customerRecipeId: number, pairFrameLens: PairFrameLens) {
    const linkingRequest = new ProductRecipeLinkingRequest();
    linkingRequest.customerRecipeId = customerRecipeId;
    linkingRequest.frameSku = pairFrameLens.frameSku;
    linkingRequest.lensSku = pairFrameLens.lensSku;
    linkingRequest.pairNumber = pairFrameLens.pairNumber;
    linkingRequest.items = pairFrameLens.items.map(pfli => {
      const item = new ProductRecipeLinkingRequestItem();
      item.orderItemId = pfli.id;
      return item;
    });
    return linkingRequest;
  }
}

export { CustomerRecipeLinkingForm };